import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Bell, Check, CheckCheck } from "lucide-react";
import { useNavigate } from "react-router-dom";

const KIND_COLORS = {
  mention: "bg-orange-100 text-orange-700",
  dm: "bg-blue-100 text-blue-700",
  task: "bg-emerald-100 text-emerald-700",
  thread: "bg-purple-100 text-purple-700",
  shipment_risk: "bg-red-100 text-red-700",
  escalation: "bg-red-100 text-red-700",
};

// Same-tab pub/sub so the sidebar badge updates instantly when a notification
// is read from NotificationsView — sidesteps any WS closure/timing edge case
// where the `unread.summary` event handler doesn't reach setNotifCount in
// time (iter21 exposed this).
function _emitUnread(count) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("tovra:notif-unread", { detail: { count } }));
}

export default function NotificationsView() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();
  const load = async () => {
    const { data } = await api.get("/notifications");
    setItems(data.notifications);
    _emitUnread(data.unread_count);
  };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => {
    const { data } = await api.post(`/notifications/${id}/read`);
    if (data?.summary) _emitUnread(data.summary.notifications_total ?? 0);
    load();
  };
  const markAll = async () => {
    const { data } = await api.post("/notifications/read-all");
    if (data?.summary) _emitUnread(data.summary.notifications_total ?? 0);
    load();
  };
  return (
    <div className="flex-1 flex flex-col overflow-hidden bg-white" data-testid="notifs-view">
      <div className="h-14 border-b flex items-center justify-between px-6 shrink-0">
        <h1 className="text-lg font-semibold">Notifications</h1>
        <Button size="sm" variant="outline" onClick={markAll} data-testid="notif-mark-all"><CheckCheck className="w-4 h-4 mr-1"/> Mark all read</Button>
      </div>
      <div className="flex-1 overflow-auto scroll-thin p-6 max-w-2xl">
        {items.length === 0 && <div className="text-sm text-zinc-400">Nothing yet.</div>}
        <div className="space-y-2">
          {items.map((n) => (
            <div key={n.id} onClick={() => { if (n.link) nav(n.link.replace("/app", "/app")); markRead(n.id); }}
              data-testid={`notif-${n.id}`}
              className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-zinc-50 ${n.read ? "opacity-70" : ""}`}>
              <span className={`text-[10px] rounded-full px-2 py-0.5 mt-0.5 ${KIND_COLORS[n.kind] || "bg-zinc-100 text-zinc-700"}`}>{n.kind}</span>
              <div className="flex-1">
                <div className="text-sm font-medium">{n.title}</div>
                {n.body && <div className="text-xs text-zinc-500 mt-0.5">{n.body}</div>}
                <div className="text-[10px] text-zinc-400 mt-1">{new Date(n.created_at).toLocaleString()}</div>
              </div>
              {!n.read && <span className="w-2 h-2 rounded-full bg-[var(--brand-accent)] mt-2"/>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
